// src/pages/AttendancePage/AttendancePage.jsx
import React, { useState, useCallback, useMemo, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faUserCheck,
	faCalendarDay,
	faCalendarWeek,
	faChevronLeft,
	faChevronRight,
	faSearch,
	faUsers,
	faCheck,
	faTimes,
	faExclamationTriangle,
	faInfoCircle,
	faSave,
	faUndoAlt,
} from "@fortawesome/free-solid-svg-icons";

import { PageContainer } from "../../components/layout";
import { Loading, Button, Modal } from "../../components/common";
import { useUpdateAbsenceStatusMutation } from "../../store/api";
import { useAttendanceData } from "./hooks/useAttendanceData";
import MobileAttendanceList from "./components/MobileAttendanceList";
import styles from "./AttendancePage.module.css";

const VIEW_MODES = {
	DAILY: "daily",
	WEEKLY: "weekly",
};

const PERIODS = [1, 2, 3, 4, 5, 6, 7];
const PERIODS_FRIDAY = [1, 2, 3, 4, 5]; // Friday has only 5 periods
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri"];

const AttendancePage = () => {
	const { students, absenceMap, classLogMap, isLoading, error } =
		useAttendanceData();

	const [viewMode, setViewMode] = useState(VIEW_MODES.DAILY);
	const [currentDate, setCurrentDate] = useState(new Date());
	const [searchTerm, setSearchTerm] = useState("");
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [modalData, setModalData] = useState(null);
	const [modalReason, setModalReason] = useState("");
	const [pendingChanges, setPendingChanges] = useState({});
	const [showPendingBanner, setShowPendingBanner] = useState(false);
	const [isMobileView, setIsMobileView] = useState(false);

	const [updateAbsenceStatus] = useUpdateAbsenceStatusMutation();

	// Check if current date is Friday
	const isFriday = currentDate.getDay() === 5;

	// Handle responsive detection
	useEffect(() => {
		const handleResize = () => {
			setIsMobileView(window.innerWidth <= 968);
		};

		handleResize(); // Check initial size
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	// Format date for display and storage
	const formatDate = useCallback((date) => {
		return date.toISOString().split("T")[0];
	}, []);

	const formatDisplayDate = useCallback((date) => {
		return new Intl.DateTimeFormat("en-US", {
			weekday: "long",
			year: "numeric",
			month: "long",
			day: "numeric",
		}).format(date);
	}, []);

	// Check if current date is Friday to determine periods
	const getCurrentPeriods = useCallback(() => {
		const dayOfWeek = currentDate.getDay();
		return dayOfWeek === 5 ? PERIODS_FRIDAY : PERIODS;
	}, [currentDate]);

	// Navigation functions
	const navigateDate = useCallback(
		(direction) => {
			const newDate = new Date(currentDate);
			newDate.setDate(currentDate.getDate() + direction);
			setCurrentDate(newDate);
		},
		[currentDate]
	);

	const goToToday = useCallback(() => {
		setCurrentDate(new Date());
	}, []);

	// Filter students
	const filteredStudents = useMemo(() => {
		if (!searchTerm.trim()) return students;
		const term = searchTerm.toLowerCase();
		return students.filter(
			(student) =>
				student.name.toLowerCase().includes(term) ||
				student.studentId.toLowerCase().includes(term)
		);
	}, [students, searchTerm]);

	// Get attendance status for a student and period
	const getAttendanceStatus = useCallback(
		(studentId, period) => {
			const dateKey = formatDate(currentDate);
			const classLogKey = `${dateKey}-${period}`;
			const absenceKey = `${dateKey}-${period}-${studentId}`;

			// Check if there are pending changes first
			if (pendingChanges[absenceKey]) {
				const change = pendingChanges[absenceKey];
				if (change.isExcused === true) return "excused";
				if (change.isExcused === false) return "unexcused";
				if (change.isExcused === null) return "pending";
			}

			// Check if class was held this period
			const classWasHeld = classLogMap.has(classLogKey);
			if (!classWasHeld) {
				return "no-class"; // No class held
			}

			// Check if student was absent
			const absenceData = absenceMap.get(absenceKey);
			if (!absenceData) {
				return "present"; // Class was held, student not marked absent = present
			}

			// Student was marked absent, check excuse status
			if (absenceData.isExcused === true) return "excused";
			if (absenceData.isExcused === false) return "unexcused";
			return "pending"; // isExcused is null
		},
		[formatDate, currentDate, absenceMap, classLogMap, pendingChanges]
	);

	// Handle status change
	const handleStatusChange = useCallback(
		async (studentId, period, isExcused, reason) => {
			const dateKey = formatDate(currentDate);
			const absenceKey = `${dateKey}-${period}-${studentId}`;
			const existingAbsence = absenceMap.get(absenceKey);

			if (existingAbsence && existingAbsence.absenceId) {
				try {
					await updateAbsenceStatus({
						absenceId: existingAbsence.absenceId,
						isExcused,
						reason: reason || existingAbsence.reason,
					}).unwrap();

					// Close modal
					setIsModalOpen(false);
					setModalData(null);
					setModalReason("");
				} catch (error) {
					console.error("Failed to update absence:", error);
				}
			} else {
				setPendingChanges((prev) => ({
					...prev,
					[absenceKey]: {
						studentId,
						period,
						isExcused,
						reason: reason || "",
					},
				}));
				setShowPendingBanner(true);

				// Close modal
				setIsModalOpen(false);
				setModalData(null);
				setModalReason("");
			}
		},
		[formatDate, currentDate, absenceMap, updateAbsenceStatus]
	);

	// Open status change modal
	const openStatusModal = useCallback(
		(studentId, period) => {
			const student = students.find((s) => s.id === studentId);
			const dateKey = formatDate(currentDate);
			const absenceKey = `${dateKey}-${period}-${studentId}`;
			const existingAbsence = absenceMap.get(absenceKey);

			setModalData({
				studentId,
				studentName: student?.name,
				period,
				date: dateKey,
				currentStatus: getAttendanceStatus(studentId, period),
				existingAbsence,
			});
			setModalReason(existingAbsence?.reason || "");
			setIsModalOpen(true);
		},
		[students, formatDate, currentDate, absenceMap, getAttendanceStatus]
	);

	// Save pending changes (for creating new absences)
	const savePendingChanges = useCallback(() => {
		// Here you would send the pending changes to your backend
		console.log("Saving pending changes:", pendingChanges);

		// For now, just clear pending changes (you'll need to implement the API call)
		setPendingChanges({});
		setShowPendingBanner(false);
	}, [pendingChanges]);

	// Discard pending changes
	const discardPendingChanges = useCallback(() => {
		setPendingChanges({});
		setShowPendingBanner(false);
	}, []);

	// Get attendance summary for header
	const getAttendanceSummary = useMemo(() => {
		const currentPeriods = getCurrentPeriods();
		let totalPresent = 0;
		let totalAbsent = 0;
		let totalPending = 0;
		let totalNoClass = 0;

		filteredStudents.forEach((student) => {
			currentPeriods.forEach((period) => {
				const status = getAttendanceStatus(student.id, period);
				if (status === "present") totalPresent++;
				else if (status === "pending") totalPending++;
				else if (status === "no-class") totalNoClass++;
				else totalAbsent++;
			});
		});

		const totalWithClass = totalPresent + totalAbsent + totalPending;
		const attendanceRate =
			totalWithClass > 0
				? ((totalPresent / totalWithClass) * 100).toFixed(1)
				: 0;

		return {
			present: totalPresent,
			absent: totalAbsent,
			pending: totalPending,
			noClass: totalNoClass,
			rate: attendanceRate,
		};
	}, [filteredStudents, getCurrentPeriods, getAttendanceStatus]);

	// Render attendance cell
	const renderAttendanceCell = useCallback(
		(studentId, period) => {
			const status = getAttendanceStatus(studentId, period);
			const dateKey = formatDate(currentDate);
			const absenceKey = `${dateKey}-${period}-${studentId}`;
			const hasPendingChange = pendingChanges[absenceKey];

			if (status === "no-class") {
				// No class held - empty cell
				return <div className={styles.attendanceCell}></div>;
			}

			return (
				<div
					className={`${styles.attendanceCell} ${styles[status]} ${
						hasPendingChange ? styles.pending : ""
					}`}
					onClick={() =>
						status !== "present" ? openStatusModal(studentId, period) : null
					}
					title={
						status === "present"
							? "Present"
							: status === "pending"
							? "Pending - Click to update"
							: status === "excused"
							? "Excused Absence"
							: status === "unexcused"
							? "Unexcused Absence"
							: "Click to update status"
					}
				>
					{status === "present" && (
						<div className={styles.presentIndicator}>
							<FontAwesomeIcon icon={faCheck} />
						</div>
					)}
					{status === "pending" && (
						<div className={styles.pendingIndicator}>
							<FontAwesomeIcon icon={faExclamationTriangle} />
							{hasPendingChange && (
								<FontAwesomeIcon
									icon={faSave}
									className={styles.pendingEditIcon}
								/>
							)}
						</div>
					)}
					{status === "excused" && (
						<div className={styles.excusedIndicator}>
							<FontAwesomeIcon icon={faCheck} />
						</div>
					)}
					{status === "unexcused" && (
						<div className={styles.unexcusedIndicator}>
							<FontAwesomeIcon icon={faTimes} />
						</div>
					)}
				</div>
			);
		},
		[
			getAttendanceStatus,
			formatDate,
			currentDate,
			pendingChanges,
			openStatusModal,
		]
	);

	if (isLoading) {
		return (
			<PageContainer>
				<Loading />
			</PageContainer>
		);
	}

	if (error) {
		return (
			<PageContainer>
				<div className={styles.errorState}>
					<h2>Error loading attendance data</h2>
					<p>Unable to load attendance information. Please try again.</p>
				</div>
			</PageContainer>
		);
	}

	const currentPeriods = getCurrentPeriods();
	const isToday = formatDate(currentDate) === formatDate(new Date());

	return (
		<PageContainer>
			<div className={styles.attendancePage}>
				{/* Header - only show on desktop */}
				{!isMobileView && (
					<div className={styles.header}>
						<div className={styles.headerLeft}>
							<h1 className={styles.title}>
								<FontAwesomeIcon icon={faUserCheck} />
								Attendance Management
							</h1>
							<p className={styles.subtitle}>
								Department - Track and manage student attendance
							</p>
						</div>

						<div className={styles.headerRight}>
							<div className={styles.summaryStats}>
								<div className={styles.stat}>
									<span className={styles.statValue}>
										{getAttendanceSummary.rate}%
									</span>
									<span className={styles.statLabel}>Present</span>
								</div>
								<div className={styles.stat}>
									<span className={styles.statValue}>
										{getAttendanceSummary.pending}
									</span>
									<span className={styles.statLabel}>Pending</span>
								</div>
							</div>

							<div className={styles.viewToggle}>
								<button
									className={`${styles.viewToggleBtn} ${
										viewMode === VIEW_MODES.DAILY ? styles.active : ""
									}`}
									onClick={() => setViewMode(VIEW_MODES.DAILY)}
								>
									<FontAwesomeIcon icon={faCalendarDay} />
									Daily
								</button>

								{/* TODO: Implement weekly view toggle */}
								{/* <button
									className={`${styles.viewToggleBtn} ${
										viewMode === VIEW_MODES.WEEKLY ? styles.active : ""
									}`}
									onClick={() => setViewMode(VIEW_MODES.WEEKLY)}
								>
									<FontAwesomeIcon icon={faCalendarWeek} />
									Weekly
								</button> */}
							</div>
						</div>
					</div>
				)}

				{/* Pending Changes Banner */}
				{showPendingBanner && (
					<div className={styles.pendingBanner}>
						<div className={styles.pendingContent}>
							<FontAwesomeIcon icon={faInfoCircle} />
							<span>
								You have {Object.keys(pendingChanges).length} unsaved changes
							</span>
						</div>
						<div className={styles.pendingActions}>
							<Button
								variant="primary"
								size="small"
								onClick={savePendingChanges}
							>
								<FontAwesomeIcon icon={faSave} />
								Save Changes
							</Button>
							<Button
								variant="secondary"
								size="small"
								onClick={discardPendingChanges}
							>
								<FontAwesomeIcon icon={faUndoAlt} />
								Discard
							</Button>
						</div>
					</div>
				)}

				{/* Controls */}
				<div className={styles.controls}>
					<div className={styles.navigation}>
						<button
							className={styles.navBtn}
							onClick={() => navigateDate(-1)}
							title="Previous day"
						>
							<FontAwesomeIcon icon={faChevronLeft} />
						</button>

						<div className={styles.dateContainer}>
							<h2 className={styles.dateTitle}>
								{formatDisplayDate(currentDate)}
								{isToday && <span className={styles.todayBadge}>Today</span>}
							</h2>

							{!isToday && (
								<div className={styles.todayButton}>
									<Button variant="secondary" size="small" onClick={goToToday}>
										Today
									</Button>
								</div>
							)}
						</div>

						<button
							className={styles.navBtn}
							onClick={() => navigateDate(1)}
							title="Next day"
						>
							<FontAwesomeIcon icon={faChevronRight} />
						</button>
					</div>

					{/* Search - only show on desktop */}
					{!isMobileView && (
						<div className={styles.searchAndFilter}>
							<div className={styles.searchContainer}>
								<FontAwesomeIcon
									icon={faSearch}
									className={styles.searchIcon}
								/>
								<input
									type="text"
									placeholder="Search students..."
									value={searchTerm}
									onChange={(e) => setSearchTerm(e.target.value)}
									className={styles.searchInput}
								/>
							</div>
						</div>
					)}
				</div>

				{/* Attendance Content - conditional rendering */}
				{isMobileView ? (
					<MobileAttendanceList
						students={filteredStudents}
						currentDate={currentDate}
						formatDate={formatDate}
						getCurrentPeriods={getCurrentPeriods}
						getAttendanceStatus={getAttendanceStatus}
						openStatusModal={openStatusModal}
					/>
				) : (
					/* Desktop Attendance Table */
					<div className={styles.attendanceContainer}>
						<div className={styles.tableWrapper}>
							{/* Table Header */}
							<div
								className={`${styles.tableHeader} ${
									isFriday ? styles.fridayHeader : ""
								}`}
							>
								<div className={styles.studentColumn}>
									<FontAwesomeIcon icon={faUsers} />
									Students ({filteredStudents.length})
								</div>
								{viewMode === VIEW_MODES.DAILY
									? currentPeriods.map((period) => (
											<div key={period} className={styles.periodColumn}>
												Period {period}
											</div>
									  ))
									: WEEKDAYS.map((day) => (
											<div key={day} className={styles.dayColumn}>
												{day}
											</div>
									  ))}
							</div>

							{/* Table Body */}
							<div className={styles.tableBody}>
								{filteredStudents.map((student) => (
									<div
										key={student.id}
										className={`${styles.studentRow} ${
											isFriday ? styles.fridayRow : ""
										}`}
									>
										<div className={styles.studentName}>
											<span className={styles.studentNameText}>
												{student.name}
											</span>
											<span className={styles.studentId}>
												{student.studentId}
											</span>
										</div>

										{viewMode === VIEW_MODES.DAILY
											? currentPeriods.map((period) => (
													<div key={period} className={styles.periodCell}>
														{renderAttendanceCell(student.id, period)}
													</div>
											  ))
											: WEEKDAYS.map((day) => (
													<div key={day} className={styles.dayCell}>
														{/* Weekly view implementation */}
														<div className={styles.weeklyCount}>-</div>
													</div>
											  ))}
									</div>
								))}

								{filteredStudents.length === 0 && (
									<div className={styles.emptyState}>
										<FontAwesomeIcon icon={faUsers} />
										<p>No students found matching your search.</p>
									</div>
								)}
							</div>
						</div>
					</div>
				)}

				{/* Status Change Modal */}
				<Modal
					isOpen={isModalOpen}
					onClose={() => {
						setIsModalOpen(false);
						setModalData(null);
						setModalReason("");
					}}
					title="Update Attendance Status"
					size="medium"
				>
					{modalData && (
						<div className={styles.modalContent}>
							<div className={styles.modalInfo}>
								<div className={styles.studentInfo}>
									<h3>{modalData.studentName}</h3>
									<p>
										Period {modalData.period} • {formatDisplayDate(currentDate)}
									</p>
								</div>
							</div>

							<div className={styles.statusOptions}>
								<h4>Select Status:</h4>
								<div className={styles.statusButtons}>
									<button
										className={`${styles.statusBtn} ${styles.excusedBtn}`}
										onClick={() =>
											handleStatusChange(
												modalData.studentId,
												modalData.period,
												true,
												modalReason
											)
										}
									>
										<FontAwesomeIcon icon={faCheck} />
										Mark as Excused
									</button>
									<button
										className={`${styles.statusBtn} ${styles.unexcusedBtn}`}
										onClick={() =>
											handleStatusChange(
												modalData.studentId,
												modalData.period,
												false,
												modalReason
											)
										}
									>
										<FontAwesomeIcon icon={faTimes} />
										Mark as Not Excused
									</button>
									<button
										className={`${styles.statusBtn} ${styles.pendingBtn}`}
										onClick={() =>
											handleStatusChange(
												modalData.studentId,
												modalData.period,
												null,
												modalReason
											)
										}
									>
										<FontAwesomeIcon icon={faExclamationTriangle} />
										Keep as Pending
									</button>
								</div>
							</div>

							<div className={styles.reasonSection}>
								<label htmlFor="reason">Reason/Notes (optional):</label>
								<textarea
									id="reason"
									value={modalReason}
									onChange={(e) => setModalReason(e.target.value)}
									placeholder="Enter reason for absence or any additional notes..."
									rows={3}
									className={styles.reasonTextarea}
								/>
							</div>

							{modalData.existingAbsence?.reason && (
								<div className={styles.existingReason}>
									<strong>Current reason:</strong>
									<p>{modalData.existingAbsence.reason}</p>
								</div>
							)}
						</div>
					)}
				</Modal>
			</div>
		</PageContainer>
	);
};

export default AttendancePage;
